import React, {useContext, useEffect, useMemo, useState} from 'react';
import {AppContext} from '../context/AppContext';
import {describeSourceLabel, DownloadManifest, loadDownloadsManifest} from '../services/Download';

type OS = 'windows' | 'mac' | 'linux' | 'android' | 'ios' | 'other';
type Arch = 'arm64' | 'x64' | 'unknown';

const detectOSArch = (): { os: OS; arch: Arch } => {
  const ua = typeof navigator !== 'undefined' ? navigator.userAgent : '';
  const plat = (navigator as any).userAgentData?.platform || (navigator as any).platform || '';

  const p = `${ua} ${plat}`.toLowerCase();

  let os: OS = 'other';
  if (p.includes('windows')) os = 'windows';
  else if (p.includes('mac') || p.includes('darwin')) os = 'mac';
  else if (p.includes('linux') || p.includes('x11')) os = 'linux';
  else if (p.includes('android')) os = 'android';
  else if (p.includes('iphone') || p.includes('ipad') || p.includes('ios')) os = 'ios';

  let arch: Arch = 'unknown';
  const uaArch = (navigator as any).userAgentData?.architecture as string | undefined;
  if (uaArch) {
    if (uaArch.toLowerCase().includes('arm')) arch = 'arm64';
    else if (uaArch.toLowerCase().includes('x86') || uaArch.toLowerCase().includes('x64')) arch = 'x64';
  } else {
    if (/arm64|aarch64|apple silicon/i.test(ua)) arch = 'arm64';
    else if (/x86_64|win64|x64|amd64/i.test(ua)) arch = 'x64';
  }

  return {os, arch};
};

const pickMacLink = (m: DownloadManifest['mac'] | undefined, arch: Arch, t: (k: string, d?: string) => string): {
  url?: string;
  label: string
} => {
  if (!m) return {url: undefined, label: t('download')};
  if (m.universal) return {url: m.universal, label: t('universalDmg')};
  if (arch === 'arm64' && m.appleSilicon) return {url: m.appleSilicon, label: t('appleSiliconDmg')};
  if (arch === 'x64' && m.intel) return {url: m.intel, label: t('intelDmg')};
  return {
    url: m.appleSilicon || m.intel || m.zip,
    label: m.appleSilicon ? t('appleSiliconDmg') : m.intel ? t('intelDmg') : t('macosZip')
  };
};

const pickWindowsLink = (m: DownloadManifest['windows'] | undefined, t: (k: string, d?: string) => string): {
  url?: string;
  label: string
} => {
  if (!m) return {url: undefined, label: t('download')};
  return m.exe ? {url: m.exe, label: t('windowsExe')} : m.msi ? {url: m.msi, label: t('msiInstaller')} : {
    url: undefined,
    label: t('download')
  };
};

const DownloadsView: React.FC = () => {
  const context = useContext(AppContext);
  const i18n = context?.i18n;
  const t = (k: string, d?: string) => (i18n ? i18n.t(k) : d || k);

  const [loading, setLoading] = useState(true);
  const [source, setSource] = useState<'remote' | 'env'>('env');
  const [manifest, setManifest] = useState<DownloadManifest>({windows: {}, mac: {}, chrome: {}});

  const {os, arch} = useMemo(detectOSArch, []);
  const recommendedLabel = os === 'windows' ? t('windows') : os === 'mac' ? t('macos') : t('downloads');

  useEffect(() => {
    let mounted = true;
    (async () => {
      // Load downloads manifest
      const {manifest: m, source: s} = await loadDownloadsManifest();
      if (!mounted) return;
      setManifest(m);
      setSource(s);
      setLoading(false);
    })();
    return () => {
      mounted = false;
    };
  }, []);

  // const winPrimary = pickWindowsLink(manifest.windows, t);
  // const macPrimary = pickMacLink(manifest.mac, arch, t);

  return (
    <section className="downloads-page">
      <div className="downloads-hero">
        <h2>{t('downloadFalcon')} {manifest.version ? `v${manifest.version}` : ''}</h2>
        <p>
          {t('pickPlatform')}
          {manifest.timestamp ? ` • ${t('released')} ${new Date(manifest.timestamp * 1000).toLocaleDateString()}` : ''}
        </p>
        {!loading && (
          <p className="downloads-footnote">
            {t('source')}: {describeSourceLabel(source)}
          </p>
        )}
      </div>

      {loading ? (
        <div className="download-cards" aria-busy="true">
          <article className="download-card">
            <div className="card-badge">{t('loading')}</div>
            <div className="card-icon"/>
            <h3>{t('resolvingLinks')}</h3>
            <p>{t('pleaseWait')}</p>
            <div className="card-actions">
              <button className="btn primary" disabled>{t('download')}</button>
              <button className="btn outline" disabled>{t('otherOptions')}</button>
            </div>
          </article>
        </div>
      ) : (
        <div className="download-cards">
          <article className={`download-card ${os === 'windows' ? 'recommended' : ''}`}>
            <div className="card-badge">{os === 'windows' ? t('recommended') : t('windows')}</div>
            <div className="card-icon win" aria-hidden/>
            <h3>{t('windows')}</h3>
            <p>{t('windowsDesc')}</p>
            <div className="card-actions">
              {/*{winPrimary.url ? (*/}
              {/*  <a className="btn primary" href={winPrimary.url}>*/}
              {/*    {winPrimary.label}*/}
              {/*  </a>*/}
              {/*) : (*/}
              {/*  <button className="btn primary" disabled>{t('unavailable')}</button>*/}
              {/*)}*/}
              {manifest.windows?.exe && (
                <a className="btn outline" href={manifest.windows.exe}>
                  {t('windowsExe')}
                </a>
              )}
              {manifest.windows?.msi && (
                <a className="btn outline" href={manifest.windows.msi}>
                  {t('msiInstaller')}
                </a>
              )}
            </div>
          </article>

          <article className={`download-card ${os === 'mac' ? 'recommended' : ''}`}>
            <div className="card-badge">{os === 'mac' ? t('recommended') : t('macos')}</div>
            <div className="card-icon mac" aria-hidden/>
            <h3>{t('macos')}</h3>
            <p>{t('macosDesc')}</p>
            <div className="card-actions">
              {/*{macPrimary.url ? (*/}
              {/*  <a className="btn primary" href={macPrimary.url}>*/}
              {/*    {macPrimary.label}*/}
              {/*  </a>*/}
              {/*) : (*/}
              {/*  <button className="btn primary" disabled>{t('unavailable')}</button>*/}
              {/*)}*/}
              {manifest.mac?.appleSilicon && (
                <a className="btn outline" href={manifest.mac.appleSilicon}>{t('appleSiliconDmg')}</a>
              )}
              {manifest.mac?.intel && (
                <a className="btn outline" href={manifest.mac.intel}>{t('intelDmg')}</a>
              )}
              {manifest.mac?.zip && (
                <div className="row" style={{justifyContent: 'space-between', alignItems: 'center'}}>
                  <a className="btn outline" href={manifest.mac.dmg}>{t('macosDmg')}</a>
                  <a className="btn outline" href={manifest.mac.zip}>{t('macosZip')}</a>
                </div>
            )}
            </div>
          </article>

          <article className="download-card">
            <div className="card-badge">{t('browser')}</div>
            <div className="card-icon chrome" aria-hidden/>
            <h3>{t('browserExtension')}</h3>
            <p>{t('browserExtDesc')}</p>
            <div className="card-actions">
              {manifest.chrome?.zip ? (
                <a className="btn outline" href={manifest.chrome.zip}>
                  {t('browserZip')}
                </a>
              ) : (
                <button className="btn primary" disabled>{t('unavailable')}</button>
              )}
            </div>
          </article>
        </div>
      )}

      <div className="downloads-footnotes">
        <p>
          {t('recommended')}: {recommendedLabel} ({arch})
        </p>
      </div>
    </section>
  );
};

export default DownloadsView;
